import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Customer, Invoice } from '../types';
import { api } from '../services/api';
import './BillingPage.css';

interface CustomerInvoices {
  customer: Customer;
  invoices: Invoice[];
}

const formatDate = (dateStr: string | null | undefined): string => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const formatCurrency = (amount: number): string =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

const getStatusBadgeClass = (invoice: Invoice): string => {
  const status = invoice.status;
  if (status === 'paid' || invoice.datePaid) return 'paid';
  if (status === 'void') return 'void';
  if (status === 'overdue') return 'overdue';
  return 'unpaid';
};

const getStatusLabel = (invoice: Invoice): string => {
  if (invoice.status === 'void') return 'Void';
  if (invoice.datePaid || invoice.status === 'paid') return 'Paid';
  if (invoice.status === 'overdue') return 'Overdue';
  return 'Unpaid';
};

export const BillingPage = () => {
  const [customerInvoices, setCustomerInvoices] = useState<CustomerInvoices[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    if (!user?.customers?.length) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const results = await Promise.all(
        user.customers.map(async (customer) => {
          const invoices = await api.getInvoices(customer.id);
          return { customer, invoices };
        })
      );

      setCustomerInvoices(results.filter((r) => r.invoices.length > 0));
    } catch (err) {
      setError('Failed to load invoices. Please try again later.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="billing-page">
        <div className="loading">
          <div className="spinner"></div>
          <p>Loading invoices...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="billing-page">
        <div className="error">
          <p>{error}</p>
          <button onClick={fetchInvoices} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  const hasInvoices = customerInvoices.some((ci) => ci.invoices.length > 0);

  return (
    <div className="billing-page">
      <div className="page-header">
        <h1>Billing</h1>
        <p className="subtitle">View invoices for your account</p>
      </div>

      {!hasInvoices ? (
        <div className="no-results">
          <p>No invoices found.</p>
        </div>
      ) : (
        <div className="billing-container">
          {customerInvoices.map(({ customer, invoices: rawInvoices }) => {
            const invoices = [...rawInvoices].sort(
              (a, b) => new Date(b.dateInvoiced).getTime() - new Date(a.dateInvoiced).getTime()
            );
            return (
            <div key={customer.id} className="billing-section">
              {customerInvoices.length > 1 && (
                <h3 className="customer-heading">{customer.name}</h3>
              )}
              <div className="invoice-table-wrapper">
                <table className="invoice-table">
                  <thead>
                    <tr>
                      <th>Invoice #</th>
                      <th>Date Invoiced</th>
                      <th>Due Date</th>
                      <th>Date Paid</th>
                      <th className="amount-col">Amount</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map((invoice) => (
                      <tr
                        key={invoice.id}
                        className="invoice-row"
                        onClick={() => navigate(`/invoices/${invoice.id}`)}
                      >
                        <td className="invoice-number">{invoice.invoiceNumber}</td>
                        <td>{formatDate(invoice.dateInvoiced)}</td>
                        <td>{formatDate(invoice.dueDate)}</td>
                        <td>{formatDate(invoice.datePaid)}</td>
                        <td className="amount-col">{formatCurrency(invoice.amount)}</td>
                        <td>
                          <span className={`status-badge ${getStatusBadgeClass(invoice)}`}>
                            {getStatusLabel(invoice)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
